import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DEMO_COMMUNITY, 
  DEMO_BADGES, 
  getDemoHolderCount,
  DEMO_PUBKEYS,
  getDemoProfile 
} from '@/lib/demoData';

export default function BadgesPage() {
  const holders = getDemoHolderCount();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-violet-900 via-indigo-900 to-purple-900">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500 rounded-full blur-3xl transform -translate-y-1/2" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-500 rounded-full blur-3xl transform translate-y-1/2" />
        </div>

        <div className="relative z-10 px-6 py-16 md:px-12 max-w-6xl mx-auto">
          <div className="text-center">
            <Badge className="mb-4 bg-white/10 text-white border-0 backdrop-blur">
              NIP-XX Draft Implementation
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Hierarchical Badges
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              A decentralized authority system for Nostr communities. 
              Establish ranked tiers of trust with delegated badge granting.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-12">
            <div className="text-center p-4 rounded-xl bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold text-white">1</p>
              <p className="text-sm text-white/60">Community</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold text-white">5</p>
              <p className="text-sm text-white/60">Badge Tiers</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold text-white">{holders}</p>
              <p className="text-sm text-white/60">Members</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* How It Works */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <HowItWorksIcon className="w-6 h-6" />
            How Hierarchical Badges Work
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: <UsersIcon className="w-6 h-6" />,
                title: 'Founders',
                description: 'Rank 0 - Can award any badge in the hierarchy',
                color: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
              },
              {
                icon: <ShieldIcon className="w-6 h-6" />,
                title: 'Maintainers',
                description: 'Rank 1 - Can award Contributor & Member badges',
                color: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
              },
              {
                icon: <BadgeIcon className="w-6 h-6" />,
                title: 'Contributors',
                description: 'Rank 2 - Can award Member badges',
                color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
              },
              {
                icon: <UserIcon className="w-6 h-6" />,
                title: 'Members',
                description: 'Rank 3 - No awarding authority',
                color: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
              },
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl border bg-card">
                <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center mb-3`}>
                  {item.icon}
                </div>
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <FeaturesIcon className="w-6 h-6" />
            Key Features
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: 'Delegated Authority',
                description: 'Badge holders can delegate authority to others at lower ranks, creating a trust hierarchy.',
              },
              {
                title: 'Chain Validation',
                description: 'Every badge award traces back to a founder, ensuring authority flows from trusted origins.',
              },
              {
                title: 'Revocation Propagation',
                description: 'When a badge is revoked, all downstream badges automatically become invalid.',
              },
              {
                title: 'Cross-Chapter Grants',
                description: 'Authority is rank-based, not chapter-based. Contributors can award badges across chapters.',
              },
              {
                title: 'Multiple Relay Support',
                description: 'Awards are published to multiple relays for redundancy and availability.',
              },
              {
                title: 'No Central Authority',
                description: 'Built on Nostr, there is no central server or admin that can control the system.',
              },
            ].map((feature, i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Demo Community */}
        <div>
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <CommunityIcon className="w-6 h-6" />
            Demo Community
          </h2>
          
          <Card className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-64 h-48 md:h-auto bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <img
                  src={DEMO_COMMUNITY.image}
                  alt={DEMO_COMMUNITY.name}
                  className="w-32 h-32 rounded-xl border-4 border-white/20 shadow-2xl object-cover"
                />
              </div>
              <div className="flex-1 p-6">
                <CardHeader className="p-0 mb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl">{DEMO_COMMUNITY.name}</CardTitle>
                      <CardDescription className="mt-1">
                        A community for Nostr protocol developers
                      </CardDescription>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                      Demo
                    </Badge>
                  </div>
                </CardHeader>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {DEMO_BADGES.slice(0, 4).map(badge => (
                    <Badge key={badge.identifier} variant="outline" className="capitalize">
                      {badge.name} (Rank {badge.rank})
                    </Badge>
                  ))}
                  {DEMO_BADGES.length > 4 && (
                    <Badge variant="outline">+{DEMO_BADGES.length - 4} more</Badge>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex -space-x-2">
                    {[DEMO_PUBKEYS.alice, DEMO_PUBKEYS.carol, DEMO_PUBKEYS.eve].map(pubkey => {
                      const profile = getDemoProfile(pubkey);
                      return (
                        <img
                          key={pubkey}
                          src={profile.picture}
                          alt={profile.name}
                          className="w-8 h-8 rounded-full border-2 border-background"
                        />
                      );
                    })}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {holders} members
                  </span>
                </div>

                <Button asChild className="w-full md:w-auto">
                  <Link to={`/badges/${DEMO_COMMUNITY.id}`}>
                    Explore Community
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-card py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>
            Built with{" "}
            <a 
              href="https://shakespeare.diy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
            {" "}• Hierarchical Badges Demo (NIP-XX Draft)
          </p>
        </div>
      </footer>
    </div>
  );
}

// Icons
function HowItWorksIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function FeaturesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function CommunityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
